<template>
  <div
    :class="['bg-white pr-1', {'conversation-timeline': filteredConversations.length > 0 || forceShow, 'timeline-focused': forceShow}]"
  >
    <h4 class="pb-3 m-0 bg-white">
      Timeline
    </h4>
    <button
      class="btn btn-sm btn-light border text-center mw-100 btn-block mb-2"
      aria-label="Filter Conversations"
      @click="showFilterModal = true"
    >
      <span class="align-middle d-inline-block text-truncate mw-100">
        <i class="fas fa-filter text-center" />
        <span class="remove-when-narrow m-0">
          Filter <span v-if="filterCount > 0" class="badge badge-secondary">{{ filterCount }}</span>
        </span>
      </span>
    </button>
    <W2HModal
      ref="timelineFilterModal"
      :show="showFilterModal"
      title="Apply Conversation Filters"
      @cancel="showFilterModal = false"
    >
      <label class="form-group row px-3 font-weight-semibold">
        <span class="col-sm-3 col-form-label text-sm-right">Name</span>
        <multiselect
          v-model="conversationNameFilter"
          :multiple="true"
          :options="conversationNames"
          :searchable="true"
          class="col-sm-9 p-0"
          name="conversation-name"
          placeholder=""
          :hide-selected="true"
          selectLabel=""
          deselectLabel=""
        />
      </label>
      <div class="form-group row px-3">
        <span class="col-sm-3 col-form-label text-sm-right font-weight-semibold">Date Range</span>
        <input
          v-model="conversationStartFilter"
          aria-label="After this date"
          name="conversation-start-date"
          type="date"
          :max="conversationEndFilter"
          class="form-control col-sm-4"
        >
        <span class="col-sm-1 col-form-label text-sm-center font-weight-normal"> - </span>
        <input
          v-model="conversationEndFilter"
          aria-label="Before this date"
          name="conversation-end-date"
          type="date"
          :min="conversationStartFilter"
          class="form-control col-sm-4"
        >
      </div>
      <template #footer>
        <button class="btn btn-link" @click="clearFiltersOrClose()">
          {{ filterCount === 0 ? 'Cancel' : 'Clear Filters' }}
        </button>
        <button class="btn btn-primary" @click="showFilterModal = false">
          Show results
        </button>
      </template>
    </W2HModal>
    <div ref="list" class="list-group overflow-auto mb-0 pb-3">
      <ul class="m-0 p-0">
        <li v-if="conversationsLoaded && store.conversations.length === 0" class="card bg-light p-1">
          This participant has no conversations.
        </li>
        <li v-else-if="conversationsLoaded && filteredConversations.length === 0" class="card bg-light p-1">
          This participant has no conversations matching the filter(s).
        </li>
        <li
          v-for="(conversation) of filteredConversations"
          :key="conversation.id"
          :class="['conversation-card card p-2 mb-2 mt-1', isActive(conversation)? 'conversation-active' : 'bg-light', {'selected-item': isSelected(conversation.id)}]"
          @click="reselect(conversation)"
        >
          <div :ref="`conversation-${conversation.id}`" class="conversation-title">
            <p class="my-0">
              {{ inboxHelper.getDateWithOptionalYear(conversation.started_at) }}<i class="conversation-icon fas fa-comment position-absolute" />
            </p>
            <p class="hide-when-narrow mb-2 font-weight-semibold">
              {{ conversation.source_name }}
            </p>
            <p class="hide-when-narrow my-0">
              <i>{{ conversation.status }}</i> {{ formatStatus(conversation) }}
            </p>
            <span class="incident-list">
              <IncidentPill
                v-for="(incident) of (conversation.incidents)"
                :key="incident.id"
                class="hide-when-narrow d-inline-block text-truncate mw-100 mr-1"
                v-bind="incident"
              />
            </span>
          </div>
          <button
            class="btn btn-primary sr-only sr-only-focusable"
            @click="reselect(conversation)"
          >
            Jump
          </button>
        </li>
      </ul>
      <button
        v-if="activeConversation"
        class="btn btn-sm btn-danger text-center mw-100"
        aria-label="Stop Conversation"
        @click="showStopConversationModal = true"
      >
        <span class="align-middle d-inline-block text-truncate mw-100">
          <i class="fas fa-comment-slash text-center" />
          <span class="remove-when-narrow  m-0">Stop Conversation</span>
        </span>
      </button>
    </div>
    <W2HModal
      :show="showStopConversationModal"
      title="Stop Conversation"
      okText="Stop"
      okClass="btn btn-danger"
      :closeWhenOK="true"
      @ok="stopConversation"
      @update:show="showStopConversationModal = $event"
    >
      Are you sure you want to stop the conversation {{ activeConversation ? activeConversation.source_name : '' }}?
    </W2HModal>
  </div>
</template>

<script>
import IncidentPill from "@ui/IncidentPill.vue";
import W2HModal from "@ui/W2HModal.vue";
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.min.css';

export default {
  name: "ConversationTimeline",
  components: {
    W2HModal,
    IncidentPill,
    Multiselect
  },
  props: {
    store:{
      type: Object,
      required: true,
    },
    selectedConversationId: {
      type: String,
      required: true
    },
    forceShow: {
      type: Boolean,
      default: false,
    },
    inboxHelper: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      jumpedOnce: false,
      timelineHovered: false,
      conversationsLoaded: false,
      showStopConversationModal: false,
      showFilterModal: false,
      conversationNameFilter: [],
      conversationStartFilter: null,
      conversationEndFilter: null,
    }
  },
  computed: {
    lastConversation() {
      return this.store.conversations.slice(-1)[0];
    },
    // There should only be one active conversation
    activeConversation() {
      // todo: When we upgrade Node, use findLast() instead for a slight performance boost, since active conversations are more likely to be later in the list
      return this.filteredConversations.find((conversation) => this.isActive(conversation));
    },
    conversationNames() {
      return [...new Set(this.store.conversations.map((c) => c.source_name))];
    },
    filteredConversations() {
      if (this.filterCount === 0) {
        return this.store.conversations;
      }

      return this.store.conversations
          .filter(conversation => this.conversationNameFilter.length === 0 ||
              this.conversationNameFilter.includes(conversation.source_name))
          .filter(conversation => !this.conversationStartFilter ||
              this.inboxHelper.getDate(conversation.started_at).isSameOrAfter(this.conversationStartFilter, 'day') ||
              this.inboxHelper.getDate(this.getEndDate(conversation)).isSameOrAfter(this.conversationStartFilter, 'day')
          )
          .filter(conversation => !this.conversationEndFilter ||
              this.inboxHelper.getDate(conversation.started_at).isSameOrBefore(this.conversationEndFilter, 'day') ||
              this.inboxHelper.getDate(this.getEndDate(conversation)).isSameOrBefore(this.conversationEndFilter, 'day')
          );
    },
    filterCount() {
      let count = 0;
      if (this.conversationNameFilter.length > 0) {
        count++;
      }
      if (this.conversationStartFilter) {
        count++;
      }
      if (this.conversationEndFilter) {
        count++;
      }
      return count;
    },
  },
  watch: {
    forceShow(newValue) {
      if(newValue && !this.jumpedOnce) {
        this.scrollToBottom();
        this.jumpedOnce = true;
      }
    },
    filterCount: async function () {
      const el = this.$refs.list;
      const scrollTop = el.scrollTop;
      await this.$nextTick();
      if (el) {
        const scrollToOffset = el.scrollHeight - 10;
        el.scrollTo({top: this.conversationsLoaded ? scrollTop : scrollToOffset, behavior: 'instant'});
      }
    }
  },
  mounted() {
    this.loadConversations();
  },
  methods: {
    scrollToBottom() {
      const el = this.$refs.list;
      if (el) {
        const scrollToOffset = el.scrollHeight - 10;
        el.scrollTo({top: scrollToOffset, behavior: 'instant'});
      }
    },
    loadConversations() {
      this.store.getConversations().then(() => {
        this.scrollToBottom();
        this.conversationsLoaded = true;
      });
    },
    isActive(conversation) {
      return conversation.status === 'Active';
    },
    getEndDate(conversation) {
      return conversation.feedback_event?.end_date ?? conversation.expires_at;
    },
    formatStatus: function (conversation) {
      if (this.isActive(conversation)) {
        return;
      }
      return this.inboxHelper.getDateWithOptionalYear(this.getEndDate(conversation));
    },
    isSelected: function (id) {
      return id === this.selectedConversationId;
    },
    reselect: function (conversation) {
      this.$emit('reselect', conversation);
    },
    async stopConversation() {
      await this.store.stopConversation(this.activeConversation.id).catch(() => {
        alert("An error occurred while stopping the conversation. Please try again.");
      });
      this.conversationsLoaded = false;
      this.loadConversations();
    },
    clearFiltersOrClose() {
      if (this.filterCount === 0) {
        this.showFilterModal = false;
        return;
      }
      this.conversationNameFilter = [];
      this.conversationStartFilter = null;
      this.conversationEndFilter = null;
    },
  },
}
</script>

<style scoped>
.list-group {
  max-height: 70vh;
}

.conversation-card {
  white-space: nowrap;
  position: relative;
  & > :hover {
    cursor: pointer;
  }
}

.badge-pill {
  font-size: 75% !important;
  font-weight: initial !important;
  padding: .25em .4em !important;
}

/* Used conditionally on conversation-card */
.conversation-active {
  background-color: #bdf9bd;
}

.conversation-card .conversation-title > * {
  &.incident-list {
    white-space: pre-wrap;
  }

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
/* To maintain tab button functionality */
.conversation-card .jump-button {
  position: absolute;
  left: -10000px;
  top: auto;
  width: 1px;
  height: 1px;
  overflow: hidden;
}
.conversation-card:hover .jump-button, .conversation-card .jump-button:focus {
  position: absolute;
  top: 0;
  left: auto;
  right: 0;
  width: auto;
  height: auto;
}

/* Used for methods.isSelected */
.selected-item {
  outline: 3px solid cornflowerblue;
  margin-right: 3px;
  margin-left: 3px;
}

/* If there are no conversations, .conversation-timeline class is not applied */
.conversation-timeline {
  z-index: 1;
  padding-right: 0;
  padding-left: 0;
  max-width: max-content;
  /* .timeline-focused: Used conditionally on root div */

  &:focus-within, &:hover, &.timeline-focused {
    position: absolute;
    /* If max-content is large, show about 55 characters, but cut off at 60% inbox container width */
    width: min(55ch, 60cqw);

    /* Ensure potentially hidden elements are shown */
    & .hide-when-narrow {
      visibility: visible;
    }
    & .remove-when-narrow {
      display: unset;
    }
  }
}

@media (max-width: 55rem) {

  .hide-when-narrow {
    visibility: hidden;
  }
  .remove-when-narrow {
    display: none;
  }
}

.conversation-icon {
  right: .5em;
}
</style>
